import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { domainListAction } from 'src/reducers/domain/action'
import { LINK_CONTEXTS, getCourseLink } from 'src/utils/portal-links'
import usePortal from 'src/hooks/usePortal'

export const DEFAULT_DOMAIN =
  process.env.REACT_APP_VIEW_URL?.replace(/^https?:\/\//, '').replace(
    /\/$/,
    '',
  ) || 'share.minicoursegenerator.com'

const DOMAIN_QUERY = {
  CurrentPage: 0,
  PageSize: 200,
  SortingColumn: 'LastModifiedAt',
  Asc: false,
  FilterText: '',
  Filters: [],
}

/**
 * The course's two links. The direct link is on the picked domain: the
 * default, a domain that opens this course at its root, or any other verified
 * custom domain (/{code}). The portal link follows the workspace portal, under
 * the course's learning path when it is in one.
 */
const useCourseLinks = course => {
  const dispatch = useDispatch()
  const me = useSelector(state => state.authentication?.me)
  const domainState = useSelector(state => state.domain.list)
  const portal = usePortal(me?.shopKey)
  const [domain, setDomain] = useState(null)

  // Fetched on every mount: the shared list can hold one page of 5 from the custom domain settings.
  useEffect(() => {
    dispatch(domainListAction(DOMAIN_QUERY, () => {}))
  }, [dispatch])

  const verified = useMemo(
    () =>
      (domainState?.results || []).filter(
        item => Number(item.verificationStatus) === 1,
      ),
    [domainState],
  )
  const opensCourse = useCallback(
    item => String(item.defaultCourseDto?.id) === String(course.id),
    [course.id],
  )

  const domains = useMemo(
    () => [
      { label: DEFAULT_DOMAIN, kind: 'Default' },
      ...verified.map(item => ({
        label: item.domain,
        kind: opensCourse(item) ? 'Course domain' : 'Custom domain',
        root: opensCourse(item),
      })),
    ],
    [verified, opensCourse],
  )

  // A domain that opens this course is picked first.
  const picked =
    domains.find(item => item.label === domain) ||
    domains.find(item => item.root) ||
    domains[0]

  const directUrl = picked.root
    ? `https://${picked.label}`
    : `https://${picked.label}/${course.code}`
  const portalUrl = getCourseLink({
    context: LINK_CONTEXTS.PORTAL,
    course,
    portal,
    path: course.courseCollectionId ? { id: course.courseCollectionId } : null,
  })

  return {
    domain: picked.label,
    domains,
    setDomain,
    directUrl,
    portalUrl,
  }
}

/** `copied` is the key copied last, for two seconds. */
export const useCopy = () => {
  const [copied, setCopied] = useState(null)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = (key, text) => {
    navigator.clipboard?.writeText(text)
    setCopied(key)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(null), 2000)
  }

  return [copied, copy]
}

export default useCourseLinks
